import { z } from "zod";
import {
  INVESTOR_TYPES,
  ACCREDITATION_METHODS,
  US_STATES,
} from "./constants";

// Two-offering election (Phase 3). "Both" subscribes to LexGoAI's $2M
// raise AND AIDirect's $500K raise — two separate SAFEs, executed as
// independent agreements. Maps to the backend's `entity_election`
// ("AIDirect" / "LexGoAI" / "paired") at API-forward time.
export const OFFERING_CHOICES = ["LexGoAI", "AIDirect", "Both"] as const;
export type OfferingChoice = (typeof OFFERING_CHOICES)[number];

export const applySchema = z.object({
  offering: z.enum(OFFERING_CHOICES, {
    error: () => ({ message: "Choose which offering you're applying to" }),
  }),
  legalName: z.string().min(2, "Enter your legal name"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().min(7, "Enter a phone number we can reach you on"),
  state: z.enum(US_STATES, {
    error: () => ({ message: "Select your state of residence" }),
  }),
  investorType: z.enum(INVESTOR_TYPES),
  checkSize: z.enum(
    [
      "$25k–$50k",
      "$50k–$100k",
      "$100k–$250k",
      "$250k–$500k",
      "$500k–$1M",
      "$1M+",
    ],
    { error: () => ({ message: "Pick an expected check size" }) }
  ),
  vehicle: z.string().min(2, "Tell us how you'd invest (self, SDIRA, LLC, fund, etc.)"),
  sourceOfFunds: z
    .string()
    .min(20, "Briefly describe the source of the funds you'd invest")
    .max(2000),
  accreditationMethod: z.enum(ACCREDITATION_METHODS),
  consentVerification: z.literal(true, {
    error: () => ({
      message:
        "We require your consent to run third-party accreditation verification",
    }),
  }),
  consentRegD: z.literal(true, {
    error: () => ({
      message: "You must acknowledge the Reg D 506(c) terms",
    }),
  }),
  referrer: z.string().optional(),
});

export type ApplyPayload = z.infer<typeof applySchema>;

export const CHECK_SIZES = [
  "$25k–$50k",
  "$50k–$100k",
  "$100k–$250k",
  "$250k–$500k",
  "$500k–$1M",
  "$1M+",
] as const;
